import type { Company } from "src/common/search";

export const ACTIVE_TAB_STORAGE_KEY = "stock_active_tab";

/** Unified localStorage: active tab + single last data-driven selection (not per-tab map) */
export type StockActiveTabPersisted = {
  tab: string;
  lastSearchCompanyDataDriven?: Company;
};

const DEFAULT_TAB = "Derivatives";

function normalizeCompany(raw: unknown): Company | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const c = raw as Company;
  if (!c.symbol) return undefined;
  return { symbol: c.symbol, company_name: c.company_name || "" };
}

/** Parse raw localStorage; migrates legacy `dataDrivenByTab` into `lastSearchCompanyDataDriven` in-memory */
export function parseStockActiveTabStored(
  raw: string | null,
): StockActiveTabPersisted | null {
  if (raw == null || raw === "") return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed.tab !== "string") return null;

    const tab = parsed.tab;
    let lastSearchCompanyDataDriven = normalizeCompany(
      parsed.lastSearchCompanyDataDriven,
    );

    const legacyMap = parsed.dataDrivenByTab;
    if (
      !lastSearchCompanyDataDriven?.symbol &&
      legacyMap &&
      typeof legacyMap === "object" &&
      !Array.isArray(legacyMap)
    ) {
      const byTab = legacyMap as Record<string, unknown>;
      const forTab = normalizeCompany(byTab[tab]);
      const anyEntry = Object.values(byTab)
        .map(normalizeCompany)
        .find((c) => c?.symbol);
      lastSearchCompanyDataDriven = forTab?.symbol ? forTab : anyEntry;
    }

    return { tab, lastSearchCompanyDataDriven };
  } catch {
    // Legacy: plain tab name string
  }
  if (!raw.startsWith("{")) {
    return { tab: raw };
  }
  return null;
}

export function readStockActiveTabFromLocalStorage(): StockActiveTabPersisted {
  if (typeof window === "undefined") {
    return { tab: DEFAULT_TAB };
  }
  return (
    parseStockActiveTabStored(localStorage.getItem(ACTIVE_TAB_STORAGE_KEY)) ?? {
      tab: DEFAULT_TAB,
    }
  );
}

export function writeStockActiveTabToLocalStorage(
  state: StockActiveTabPersisted,
): void {
  if (typeof window === "undefined") return;
  const out: StockActiveTabPersisted = {
    tab: state.tab,
    ...(state.lastSearchCompanyDataDriven?.symbol
      ? { lastSearchCompanyDataDriven: state.lastSearchCompanyDataDriven }
      : {}),
  };
  localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, JSON.stringify(out));
}

/** Merge legacy session keys into localStorage, then remove them */
export function migrateLegacyLastSearchCompanyDataDriven(): void {
  if (typeof window === "undefined") return;

  const state = readStockActiveTabFromLocalStorage();
  let last = state.lastSearchCompanyDataDriven;
  let changed = false;

  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith("lastSearchCompanyDataDriven_")) {
      keysToRemove.push(key);
    }
  }

  let matchedTab: Company | undefined;
  let fallback: Company | undefined;

  for (const key of keysToRemove) {
    const tabPart = key.slice("lastSearchCompanyDataDriven_".length);
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      const c = normalizeCompany(JSON.parse(raw));
      if (!c) continue;
      if (tabPart === state.tab) matchedTab = c;
      else if (!fallback) fallback = c;
    } catch {
      /* ignore */
    }
    sessionStorage.removeItem(key);
  }

  const pick = matchedTab ?? fallback;
  if (pick?.symbol) {
    last = pick;
    changed = true;
  }

  const orphan = sessionStorage.getItem("lastSearchCompanyDataDriven");
  if (orphan) {
    try {
      const c = normalizeCompany(JSON.parse(orphan));
      if (c?.symbol) {
        last = c;
        changed = true;
      }
    } catch {
      /* ignore */
    }
    sessionStorage.removeItem("lastSearchCompanyDataDriven");
  }

  const rawLs = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
  if (rawLs?.includes("dataDrivenByTab")) {
    changed = true;
  }

  if (changed || rawLs?.includes("dataDrivenByTab")) {
    writeStockActiveTabToLocalStorage({
      ...state,
      lastSearchCompanyDataDriven: last,
    });
  }
}

export function setLastSearchCompanyDataDriven(company: Company): void {
  const state = readStockActiveTabFromLocalStorage();
  writeStockActiveTabToLocalStorage({
    ...state,
    lastSearchCompanyDataDriven: company.symbol
      ? {
          symbol: company.symbol,
          company_name: company.company_name || "",
        }
      : undefined,
  });
}

export function getLastSearchCompanyDataDriven(): Company | null {
  const c = readStockActiveTabFromLocalStorage().lastSearchCompanyDataDriven;
  if (c?.symbol) {
    return { symbol: c.symbol, company_name: c.company_name || "" };
  }
  return null;
}
